import './App.css';
import Menu from './Components/Menu'
import ConditionReports from './Components/ConditionReports'
import ContImprove from './Components/ContImprove'
import AddCR from './Components/AddCR'
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import CRDetails from './Components/CRDetails'
import { useMsal } from "@azure/msal-react";
import { IPublicClientApplication, NavigationClient, InteractionStatus, EventType } from "@azure/msal-browser";
import { LicenseInfo } from '@mui/x-license';
import { loginRequest } from './Components/authConfig';
import { useAuth } from './Components/AuthContext';
import { Spinner } from 'reactstrap';

LicenseInfo.setLicenseKey('a9fe658f9bca6e9ce210876d2db7325eTz0xMjU5NDcsRT0xODAzMTY3OTk5MDAwLFM9cHJvLExNPXN1YnNjcmlwdGlvbixQVj1RMy0yMDI0LEtWPTI=');

type AppProps = {
  pca: IPublicClientApplication;
};

// Authentication check component
const AuthCheck: React.FC = () => {
  const { accounts, instance, inProgress } = useMsal();
  const { setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [loginAttempted, setLoginAttempted] = useState(false);

  useEffect(() => {
    const attemptAuthentication = async () => {
      try {
        // Check if we're already handling a redirect or interaction
        if (inProgress !== InteractionStatus.None && inProgress !== InteractionStatus.SsoSilent) {
          console.log("Auth flow in progress:", inProgress);
          return; // Wait for the current auth flow to complete
        }

        setIsLoading(true);
        
        // Only handle redirect response once
        const response = await instance.handleRedirectPromise();
        
        if (response && response.account) {
          // Login successful from redirect
          console.log("Redirect authentication successful");
          setIsAuthenticated(true);
          // Use replace instead of push to avoid adding to history stack
          navigate('/conditionreports', { replace: true });
        } else if (accounts.length > 0) {
          // Already logged in with existing account
          console.log("Using existing authenticated account");
          setIsAuthenticated(true);
          navigate('/conditionreports', { replace: true });
        } else if (!loginAttempted) {
          // No authenticated user, only try login once to avoid loops
          console.log("No authenticated user, triggering login redirect");
          setLoginAttempted(true);
          
          // Store current timestamp to prevent rapid re-attempts
          sessionStorage.setItem('last_login_attempt', Date.now().toString());
          
          // Initiate login
          await instance.loginRedirect(loginRequest);
        }
      } catch (error) {
        console.error("Authentication error:", error);
        
        // Only attempt login again if we haven't already tried and if enough time has passed
        const lastAttempt = parseInt(sessionStorage.getItem('last_login_attempt') || '0');
        const timeSinceLastAttempt = Date.now() - lastAttempt;
        
        if (!loginAttempted && timeSinceLastAttempt > 10000) {
          console.log("Attempting login after error");
          setLoginAttempted(true);
          sessionStorage.setItem('last_login_attempt', Date.now().toString());
          await instance.loginRedirect(loginRequest);
        }
      } finally {
        setIsLoading(false);
      }
    };

    attemptAuthentication();
    
    // Set up event listener for when redirect completes
    const callbackId = instance.addEventCallback(event => {
      if (event.eventType === EventType.LOGIN_SUCCESS) {
        setIsAuthenticated(true);
        navigate('/conditionreports', { replace: true });
      }
    });

    // Clean up event listener
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [accounts, instance, navigate, setIsAuthenticated, inProgress, loginAttempted]);

  // Loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spinner color="primary" />
        <p style={{ marginTop: '1rem' }}>Authenticating...</p>
      </div>
    );
  }

  // This is generally not reached because we navigate away
  return null;
};

function App({ pca }: AppProps) {
  const { accounts, inProgress } = useMsal();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const navigate = useNavigate();
  const navigationClient = new NavigationClient();
  pca.setNavigationClient(navigationClient);

  useEffect(() => {
    // If we have accounts, consider the user authenticated
    if (accounts.length > 0) {
      setIsAuthenticated(true);
    }
  }, [accounts, setIsAuthenticated]);

  // Protected route component
  const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    // Only redirect if we're not in an auth flow and the user isn't authenticated
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      return <Navigate to="/" replace />;
    }
    return <>{children}</>;
  };

  return (
    <Routes>
      <Route path="/" element={<AuthCheck />} />
      <Route path="/conditionreports" element={
        <ProtectedRoute>
          <ConditionReports />
        </ProtectedRoute>
      } />
      <Route path="/contimprove" element={
        <ProtectedRoute>
          <ContImprove />
        </ProtectedRoute>
      } />
      <Route path="/conditionreports/crdetails/:data" element={
        <ProtectedRoute>
          <CRDetails />
        </ProtectedRoute>
      } />
      <Route path="/conditionreports/addcr/" element={
        <ProtectedRoute>
          <AddCR />
        </ProtectedRoute>
      } />
      <Route path="/menu/" element={
        <ProtectedRoute>
          <Menu />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
